import React from "react";
import { DateTime, DateTimeFormatOptions, LocaleOptions } from "luxon";
import { Link } from "react-router-dom";
// Components
import { AnimalPregnancyLabel, AnimalsSexClassLabel } from "components/MyLivestock";
// Types
import { KeyValuePairProps } from "components/Common/KeyValuePair";
import { GetAnimalById_animal } from "api/MyLivestock/Animal/types/GetAnimalById";
// Helpers
import { getFormattedNumber, hasFeature } from "helpers/general";
import { getAge, getAnimalBreedsAsString } from "helpers/myLivestock";
import { FEATURES } from "constants/Features";
// Utils
import { BCMSNotSync, BCMSSync } from "constants/SvgIcons";
interface AnimalInfoItems extends KeyValuePairProps {
  isHidden?: boolean;
}

export const getAnimalInfoItems = (
  animal: GetAnimalById_animal,
  isArchived: boolean,
  getFormatDate: (date: Date | string | undefined, formatOpts?: DateTimeFormatOptions, opts?: LocaleOptions) => string,
): AnimalInfoItems[][] => {
  const saleStatus = animal?.rflId ? `Assigned ${animal?.rflId || ""}` : "Not Sold";
  const showPregnancyStatus: boolean = hasFeature(FEATURES.SHOW_PREGNANCY_STATUS);

  return [
    [
      {
        label: "Passport ID",
        value: animal?.passportNumber,
      },
      {
        label: "EID",
        value: animal?.eId,
      },
      {
        label: "Visual ID",
        value: animal?.visualId,
      },
      {
        isHidden: isArchived,
        label: "Withdrawal",
        value: animal?.withdrawalEnd ? `Ends ${getFormatDate(animal?.withdrawalEnd)}` : "No",
      },
      {
        label: "Sync status",
        value: animal?.isRegulatorySynced ? <BCMSSync size="small" /> : <BCMSNotSync size="small" />,
      },
    ],
    [
      {
        label: "Breed",
        value: getAnimalBreedsAsString(animal?.animalBreeds),
      },
      {
        label: "Sex",
        value: (
          <AnimalsSexClassLabel
            isCastrated={animal?.isCastrated}
            isMale={animal?.isMale}
            label={animal?.sexClassification?.slug || null}
          />
        ),
      },
      {
        isHidden: !showPregnancyStatus || animal?.isMale,
        label: "Pregnancy status",
        value: <AnimalPregnancyLabel isMale={animal?.isMale} isPregnant={!!animal?.isPregnant} />,
      },
      {
        isHidden: !showPregnancyStatus || !animal?.pregnancyDueDate,
        label: "Pregnancy due",
        value: getFormatDate(animal?.pregnancyDueDate),
      },
      {
        label: "Sire ID",
        value: animal?.sirePassportNumber,
      },
      {
        label: "DAM ID",
        value: animal?.damPassportNumber,
      },
    ],
    [
      {
        label: "Age",
        value: animal?.dob ? getAge(DateTime.local(), animal?.dob) : null,
      },
      {
        label: "Dob",
        value: getFormatDate(animal?.dob, DateTime.DATE_SHORT, { locale: "en-US" }),
      },
      {
        label: "DoF",
        value: getFormatDate(animal?.dateMovedToFarm),
      },
      {
        isHidden: !isArchived,
        label: "Dead at date",
        value: getFormatDate(animal?.deadAt),
      },
      {
        isHidden: !isArchived,
        label: "Date left farm",
        value: getFormatDate(animal?.dateLeftFarm),
      },
    ],
    [
      {
        label: "Sale status",
        value: animal?.offerId ? <Link to={`/system/trading/offer/${animal?.offerId}`}>{saleStatus}</Link> : saleStatus,
      },
      {
        label: "Delivery date",
        value: getFormatDate(animal?.deliveryDate),
      },
      {
        label: "Buyer",
        value: animal?.buyerName,
      },
      {
        isHidden: !animal?.animalColour,
        label: "Colour",
        value: `[${animal?.animalColour?.code}] ${animal?.animalColour?.name}`,
      },
      {
        isHidden: !isArchived,
        label: "Kill weight",
        unit: "kg",
        value: animal?.killWeight ? getFormattedNumber(animal?.killWeight) : null,
      },
      {
        isHidden: !isArchived,
        label: "Kill quality",
        value: animal?.killQuality,
      },
      {
        isHidden: !isArchived,
        label: "Kill fat score",
        value: animal?.killFatScore,
      },
    ],
  ];
};
